import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

import { BACKEND_URL, API } from "../config/api";

export default function AIFeedback({ 
  feature, 
  predictionId, 
  leadId, 
  aiResponse,
  query,
  compact = false,
  onFeedbackSubmit 
}) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showComment, setShowComment] = useState(false);

  const submitFeedback = async (selectedRating) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/ai-learning/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          feature,
          prediction_id: predictionId,
          rating: selectedRating,
          comment,
          lead_id: leadId,
          ai_response: aiResponse,
          query,
        })
      });

      if (!res.ok) throw new Error('Failed to submit feedback');

      setRating(selectedRating);
      setSubmitted(true);
      setShowComment(false);
      toast.success('Thanks for your feedback!');
      
      if (onFeedbackSubmit) {
        onFeedbackSubmit(selectedRating);
      }
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-1 text-xs text-emerald-600" data-testid="ai-feedback-submitted">
        <Check className="w-3 h-3" />
        <span>Feedback recorded</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1" data-testid="ai-feedback-compact">
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 ${rating === 'positive' ? 'text-emerald-500 bg-emerald-50' : 'text-slate-400 hover:text-emerald-500'}`}
          onClick={() => submitFeedback('positive')}
          disabled={submitting}
          data-testid="ai-feedback-positive"
        >
          {submitting && rating === 'positive' ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <ThumbsUp className="w-3 h-3" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 w-6 p-0 ${rating === 'negative' ? 'text-red-500 bg-red-50' : 'text-slate-400 hover:text-red-500'}`}
          onClick={() => submitFeedback('negative')}
          disabled={submitting}
          data-testid="ai-feedback-negative"
        >
          {submitting && rating === 'negative' ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <ThumbsDown className="w-3 h-3" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid="ai-feedback">
      <span className="text-xs text-slate-500">Was this helpful?</span>
      
      <Button
        variant="ghost"
        size="sm"
        className={`h-7 px-2 ${rating === 'positive' ? 'text-emerald-500 bg-emerald-50' : 'text-slate-400 hover:text-emerald-500 hover:bg-emerald-50'}`}
        onClick={() => submitFeedback('positive')}
        disabled={submitting}
        data-testid="ai-feedback-positive"
      >
        {submitting && rating === 'positive' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
        ) : (
          <ThumbsUp className="w-3.5 h-3.5 mr-1" />
        )}
        Yes
      </Button>

      <Popover open={showComment} onOpenChange={setShowComment}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-7 px-2 ${rating === 'negative' ? 'text-red-500 bg-red-50' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'}`}
            disabled={submitting}
            data-testid="ai-feedback-negative-trigger"
          >
            <ThumbsDown className="w-3.5 h-3.5 mr-1" />
            No
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="end">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <MessageSquare className="w-4 h-4" />
              What went wrong?
            </div>
            <Textarea
              placeholder="Help us improve by sharing what was incorrect..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="text-sm min-h-[80px]"
              data-testid="ai-feedback-comment"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowComment(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => submitFeedback('negative')}
                disabled={submitting}
                className="bg-red-500 hover:bg-red-600"
                data-testid="ai-feedback-submit-negative"
              >
                {submitting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : null}
                Submit
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
